import React, { useState, useMemo } from 'react'
import {
    useRouteMatch,
    Route,
    Switch,
} from 'react-router-dom'
import { PageType, RootState, ScreenOrientation, OrganizationData, DynamicContentType } from 'app/types'
import { navigateAsync, navigateBack } from 'actions/appActions'
import { Media, Tile, OptionBar } from 'components'
import { getModelhomeFallbackTours } from 'helpers/media'

import { ModelhomeList } from './ModelhomeList'
import { ModelhomeView } from './ModelhomeView'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { logger } from 'helpers/logger'
import * as fnc from 'helpers/fnc'
import { projectContent, organizationContent } from 'app/transformers'

interface ModelhomePageProps {
    app?: AppData
    organization?: OrganizationData
    onlyFavourites: boolean
    dataLink: string
    splitIndex: number
    breadCrumbs?: boolean
}

export function ModelhomePage(props: ModelhomePageProps) {
    const { app, organization, pageLink, dataLink, extraLink, splitIndex } = props
    const dispatch = useAppDispatch()
    const media = useAppSelector((state: RootState) => state.app.media)
    const screen = useAppSelector((state: RootState) => state.app.screen)
    const links = useAppSelector((state: RootState) => state.app.links)

    // Determine which context to use (app takes priority)
    const modelhomes = app?.modelhomes || organization?.modelhomes || []
    const contextType = app ? 'app' : organization ? 'organization' : 'none'
    const contextId = app?.meta?.id || app?.id || organization?.id

    const currentPage = useMemo(
        () => (app?.pages || organization?.pages || []).find((x) => x.link === links.pageLink),
        [app, organization, links.pageLink],
    )

    const fallbackTours = useMemo(() => {
        if (modelhomes.length > 0 || !currentPage) {
            logger.info('[ModelhomePage] fallback skipped - modelhomes:', modelhomes.length, 'currentPage:', currentPage)
            return []
        }
        const dynamicContent = app?.dynamicContent || organization?.dynamicContent || []
        logger.info('[ModelhomePage] checking fallback - pageId:', currentPage.id, 'dynamicContent count:', dynamicContent.length, 'media keys:', Object.keys(media).length)
        const tours = getModelhomeFallbackTours(dynamicContent, media, currentPage.id)
        logger.info('[ModelhomePage] fallbackTours result:', tours)
        return tours
    }, [modelhomes.length, currentPage, app, organization, media])

    const [selectedFallbackLink, setSelectedFallbackLink] = useState<string | null>(null)

    const tags = [...new Set(modelhomes.map((x) => x.tag).filter((x) => x))].sort((a, b) => a.localeCompare(b))

    function handleModelhome(x) {
        
        dispatch(navigateAsync({
            app: app,
            organization: organization,
            pageType: PageType.Modelhome,
            options: { modelhomeId: x.id, splitIndex },
        })).catch(logger.error)
    }

    function handleBack() {
        dispatch(navigateBack())
    }

    function handleFullscreen(x) {
        if (screen.fullscreenSupport) {
            fnc.toggleFullscreen(x)
        } else {
            dispatch(toggleFullscreen())
        }
    }

    function getCustomContent() {
        if (app) {
            const Content = projectContent(app)
            if (Content) {
                return <Content app={app} link={links.pageLink} required={false} />
            }
        } else if (organization) {
            const Content = organizationContent(organization)
            if (Content) {
                return <Content organization={organization} link={links.pageLink} required={false} />
            }
        }
        return null
    }

    let tiling = 4
    if (screen.isMobile) {
        tiling = screen.orientation == ScreenOrientation.Portrait ? 2 : 4
    }

    if (dataLink) {
        return <ModelhomeView 
            app={app} 
            organization={organization}
            dataLink={dataLink} 
            splitIndex={splitIndex} 
            onClose={modelhomes.length > 1 ? handleBack : null} 
            onFullscreen={handleFullscreen} 
        />
    } else if (modelhomes.length == 1) {
        return <ModelhomeView 
            app={app} 
            organization={organization}
            dataLink={modelhomes[0].link} 
            splitIndex={splitIndex} 
            onFullscreen={handleFullscreen} 
        />
    } else if (modelhomes.length > 1) {
        return <React.Fragment>
            <OptionBar app={app} organization={organization} splitIndex={splitIndex} titleText={"Select a Virtual Tour"} />
            <div id="modelhomes-view" className="content scrollable">
                <div className={`content-wrapper modelhome-list tile-list by-${tiling}`}>
                    {tags.length < 2 && [...modelhomes].sort((a, b) => a.link.localeCompare(b.link)).map((x) => {
                        const tourMedia = media[x.mediaId]
                        const previewMedia = tourMedia?.previewMediaId ? media[tourMedia.previewMediaId] : tourMedia
                        return <Tile 
                            key={x.id}
                            square 
                            onClick={() => handleModelhome(x)} 
                            app={app} 
                            organization={organization}
                            media={previewMedia} 
                            title={x.name} 
                            subtitle={x.subtitle} 
                            splitTitle 
                        />
                    })}
                    {tags.length >= 2 && tags.map((tag) => {
                        const matchingModelhomes = modelhomes.filter((x) => x.tag == tag)
                        let fixedTag = tag
                        if (parseInt(tag[0]) > 0) {
                            // Remove first character
                            fixedTag = tag.slice(1, tag.length)
                        }
                        return <div key={tag} className="modelhome-group column">
                            <div className="row">
                                <h3><p>{fixedTag}</p></h3>
                            </div>
                            {/* <div className="divider" /> */}
                            <div className="row">
                                {matchingModelhomes.sort((a, b) => a.link.localeCompare(b.link)).map((x) => {
                                    const tourMedia = media[x.mediaId]
                                    const previewMedia = tourMedia?.previewMediaId ? media[tourMedia.previewMediaId] : tourMedia
                                    return <Tile 
                                        key={x.id}
                                        square 
                                        onClick={() => handleModelhome(x)} 
                                        app={app} 
                                        organization={organization}
                                        media={previewMedia} 
                                        title={x.name} 
                                        subtitle={x.subtitle} 
                                        splitTitle 
                                    />
                                })}
                            </div>
                        </div>
                    })}
                </div>
                {getCustomContent()}
            </div>
        </React.Fragment>

    }


    if (fallbackTours.length === 1) {
        return <ModelhomeView
            app={app}
            organization={organization}
            dataLink={fallbackTours[0].link}
            splitIndex={splitIndex}
            onFullscreen={handleFullscreen}
        />
    } else if (fallbackTours.length > 1) {
        if (selectedFallbackLink) {
            return <ModelhomeView
                app={app}
                organization={organization}
                dataLink={selectedFallbackLink}
                splitIndex={splitIndex}
                onClose={() => setSelectedFallbackLink(null)}
                onFullscreen={handleFullscreen}
            />
        }
        return <React.Fragment>
            <OptionBar app={app} organization={organization} splitIndex={splitIndex} titleText={"Select a Virtual Tour"} />
            <div id="modelhomes-view" className="content scrollable">
                <div className={`content-wrapper modelhome-list tile-list by-${tiling}`}>
                    {fallbackTours.map((x) => {
                        const tourMedia = media[x.mediaId]
                        return <Tile
                            key={x.id}
                            square
                            onClick={() => setSelectedFallbackLink(x.link)}
                            app={app}
                            organization={organization}
                            media={tourMedia}
                            title={x.name}
                            subtitle={x.subtitle}
                            splitTitle
                        />
                    })}
                </div>
            </div>
        </React.Fragment>
    }

    return <h3>No model homes found</h3>
}
